import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SG_FETCH_DEBRIEF_SCHEDULE_MEMBERS, DEBRIEF_SCHEDULE_MEMBERS_PAGE_NUMBER } from 'constants/actions';

// shadcn/ui components
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'components/ui/pagination';
import MultiSelect from 'components/forms/MultiSelect';

// Lucide icons
import { Search, Loader2, Users, Mail, Briefcase, UserCheck, X } from 'lucide-react';

const MemberList = ({ debriefScheduleID }) => {
  const dispatch = useDispatch();
  const { members, pending, error } = useSelector(state => state.debrief_schedule);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchBox, setSearchBox] = useState('');
  const pageSize = 12;

  // Status options for filters
  const briefTokenStatusOptions = [
    { key: 1, value: 1, text: 'Not Started / Not Submitted' },
    { key: 2, value: 2, text: 'Started / In Session' },
    { key: 3, value: 3, text: 'Completed / Submitted' },
    { key: 5, value: 5, text: 'Cancelled' },
    { key: 9, value: 9, text: 'Closed incomplete' },
  ];
  const debriefResponseStatusOptions = [
    { key: 1, value: 1, text: 'Not Started' },
    { key: 2, value: 2, text: 'Queued' },
    { key: 3, value: 3, text: 'Processing' },
    { key: 4, value: 4, text: 'Completed / Closed' },
    { key: 5, value: 5, text: 'Post Survey Actions Completed' },
    { key: 6, value: 6, text: 'feedback sentiment generated' },
    { key: 7, value: 7, text: 'redundant' },
    { key: 9, value: 9, text: 'Cancelled' },
    { key: 11, value: 11, text: 'For Deletion' },
    { key: 99, value: 99, text: 'Error' },
  ];

  const [briefTokenStatus, setBriefTokenStatus] = useState([]);
  const [debriefResponseStatus, setDebriefResponseStatus] = useState([]);

  useEffect(() => {
    if (debriefScheduleID) {
      const payload = { id: debriefScheduleID, page, page_size: pageSize };
      if (search) payload.search = search;
      if (briefTokenStatus.length > 0) payload.brief_token_status = briefTokenStatus.join(',');
      if (debriefResponseStatus.length > 0) payload.debrief_response_status = debriefResponseStatus.join(',');
      dispatch({ type: SG_FETCH_DEBRIEF_SCHEDULE_MEMBERS, payload });
    }
  }, [dispatch, debriefScheduleID, page, search, briefTokenStatus, debriefResponseStatus]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setSearch(searchBox);
    setPage(1);
  };

  useEffect(() => {
    if (members && members.count && members.page_size) {
      setTotalPages(Math.ceil(members.count / members.page_size));
    }
  }, [members]);

  const goToPage = (newPage) => {
    if (!newPage || newPage === page || newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const paginationItems = useMemo(() => {
    const pages = Number(totalPages) || 0;
    const current = Number(page) || 1;
    if (pages <= 1) return [];

    const items = [];
    const addPage = (p) => items.push({ type: "page", page: p, active: p === current });
    const addEllipsis = (key) => items.push({ type: "ellipsis", key });

    addPage(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(pages - 1, current + 1);

    if (start > 2) addEllipsis("start");
    for (let p = start; p <= end; p += 1) addPage(p);
    if (end < pages - 1) addEllipsis("end");
    if (pages > 1) addPage(pages);
    return items;
  }, [page, totalPages]);

  const handleResetFilters = () => {
    setSearchBox('');
    setSearch('');
    setBriefTokenStatus([]);
    setDebriefResponseStatus([]);
    setPage(1);
  };

  const hasResults = members?.results && members.results.length > 0;
  const hasActiveFilters = search || briefTokenStatus.length > 0 || debriefResponseStatus.length > 0;

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            <p className="font-medium">Error loading members</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Filters</CardTitle>
          <CardDescription>Search members or filter by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-[minmax(200px,1fr)_repeat(2,minmax(200px,1fr))_auto]">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchBox}
                  onChange={(e) => setSearchBox(e.target.value)}
                  placeholder="Search members..."
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            <MultiSelect
              options={briefTokenStatusOptions}
              value={briefTokenStatus}
              onChange={(value) => {
                setBriefTokenStatus(value);
                setPage(1);
              }}
              placeholder="BriefToken Status"
            />

            <MultiSelect
              options={debriefResponseStatusOptions}
              value={debriefResponseStatus}
              onChange={(value) => {
                setDebriefResponseStatus(value);
                setPage(1);
              }}
              placeholder="DebriefResponse Status"
            />

            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={handleResetFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Members</CardTitle>
              <CardDescription>
                {members?.count ? `${members.count} total member${members.count !== 1 ? 's' : ''}` : 'No members found'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {pending && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 rounded-md">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {!hasResults ? (
              <div className="py-12 text-center">
                <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-muted-foreground">No members found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {hasActiveFilters ? 'Try adjusting your filters' : 'No members are associated with this survey'}
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Account Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Email Status</TableHead>
                      <TableHead>BriefToken Status</TableHead>
                      <TableHead>DebriefResponse Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.results.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell className="font-mono text-sm">{emp?.id}</TableCell>
                        <TableCell className="font-medium">
                          {emp?.first_name || ''} {emp?.last_name || ''}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{emp?.email || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {emp?.employee_role_name ? (
                            <Badge variant="outline" className="text-xs">
                              <Briefcase className="mr-1 h-3 w-3" />
                              {emp.employee_role_name}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp?.account_type_text ? (
                            <Badge variant="secondary" className="text-xs">
                              {emp.account_type_text}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp?.status_text ? (
                            <Badge variant="outline" className="text-xs">
                              {emp.status_text}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp?.email_status_text ? (
                            <Badge variant="outline" className="text-xs">
                              {emp.email_status_text}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp?.brief_token_status ? (
                            <Badge variant="outline" className="text-xs">
                              {emp.brief_token_status}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp?.debrief_response_status ? (
                            <Badge variant="outline" className="text-xs">
                              {emp.debrief_response_status}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        goToPage(page > 1 ? page - 1 : 1);
                      }}
                      className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                  {paginationItems.map((item, idx) =>
                    item.type === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${item.key}-${idx}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={`page-${item.page}`}>
                        <PaginationLink
                          href="#"
                          isActive={item.active}
                          onClick={(e) => {
                            e.preventDefault();
                            goToPage(item.page);
                          }}
                        >
                          {item.page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        goToPage(page < totalPages ? page + 1 : totalPages);
                      }}
                      className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberList;
